<template>
  <div class="app px-5 max-w-2xl mx-auto my-20">
    <h1 class="text-center text-6xl font-bold text-secondary mb-10">QR קוד</h1>
    <p class="text-center">
      QR קוד מאפשר מעבר מהיר לקישורים, אתרים, הודעות, וואטסאפ, תשלומים ועוד — בסריקה אחת בלבד.
      <br>
      כאן תוכלו ליצור QR קוד אישי בקלות ובמהירות.
    </p>
    <h2 class="text-center text-4xl font-bold text-tertiary my-8">יצירת QR קוד משלך</h2>
    <div class="my-5">
      <buttons-group
          v-model="type"
          :options="selectOptions"
      ></buttons-group>
    </div>
    <div class="fields" v-if="type === 'link'">
      <text-field
        v-model="link"
        label="הזנ\י את הקישור שלך כאן"
        id="link"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'text'">
      <text-field
        type="textarea"
        v-model="text"
        label="הזנ\י את הטקסט שלך כאן"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'email'">
      <text-field
        v-model="email"
        type="email"
        label="הזנ\י את האימייל שלך כאן"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'phone'">
      <text-field
        v-model="phone"
        type="tel"
        label="הזנ\י את מספר הטלפון שלך כאן"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'sms'">
      <text-field
        v-model="smsNumber"
        type="tel"
        label="הזנ\י את מספר הטלפון שלך כאן"
      ></text-field>
      <text-field
        v-model="smsMessage"
        type="text"
        label="הזנ\י את ההודעה שלך כאן"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'wifi'">
      <text-field
        v-model="wifiSsid"
        type="text"
        label="הזנ\י את ה-SSID שלך כאן"
      ></text-field>
      <text-field
        v-model="wifiPassword"
        type="text"
        label="הזנ\י את הסיסמה שלך כאן"
      ></text-field>
    </div>
    <div class="fields" v-else-if="type === 'contact'">
      <text-field
        v-model="contact.name"
        type="text"
        label="הזנ\י את שמך כאן"
      ></text-field>
      <text-field
        v-model="contact.email"
        type="email"
        label="הזנ\י את האימייל שלך כאן"
      ></text-field>
      <text-field
        v-model="contact.phone"
        type="tel"
        label="הזנ\י את מספר הטלפון שלך כאן"
      ></text-field>
      <text-field
        v-model="contact.website"
        type="url"
        label="הזנ\י את האתר שלך כאן"
      ></text-field>
    </div>
    <div class="text-center">
      <button
          class="bg-amber-600 rounded-2xl px-10 py-2 text-blue-950 font-bold"
          @click="generateQRCode"
      >יצירת קוד</button>
      <p class="mt-8">QR קוד מאפשר שיתוף מידע בצורה מהירה, נגישה ונוחה - לעסקים, אתרים, הזמנות ותוכן דיגיטלי.</p>
      <div v-if="dataUri" class="results">
        <img :src="dataUri" alt="Qr Code">
        <a class="bg-amber-600 rounded-2xl px-10 py-2 text-blue-950 font-bold" :href="dataUri" download="qr-code.png">הורדת הקוד</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import QRCode from 'qrcode';
import TextField from "@/components/TextField.vue";
import ButtonsGroup from "@/components/ButtonsGroup.vue";

const type = ref('link');
const link = ref('');
const text = ref('');
const email = ref('');
const phone = ref('');
const smsNumber = ref('');
const smsMessage = ref('');
const wifiSsid = ref('');
const wifiPassword = ref('');
const contact = ref({
  name: '',
  email: '',
  phone: '',
  website: '',
});
const dataUri = ref(null);

const selectOptions = [
  { value: 'link', label: 'קישור' },
  { value: 'text', label: 'טקסט' },
  { value: 'email', label: 'אימייל' },
  { value: 'phone', label: 'טלפון' },
  { value: 'sms', label: 'הודעה' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'contact', label: 'איש קשר' }
];

const generateQRCode = () => {
  const canvas = document.createElement('canvas');
  switch(type.value){
    case 'link':
      QRCode.toCanvas(canvas, link.value, { width: 500, padding: 10 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(link.value, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'text':
      QRCode.toCanvas(canvas, text.value, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(text.value, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'email':
      QRCode.toCanvas(canvas, `mailto:${email.value}`, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(`mailto:${email.value}`, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'phone':
      QRCode.toCanvas(canvas, `tel:${phone.value}`, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(`tel:${phone.value}`, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'sms':
      QRCode.toCanvas(canvas, `smsto:${smsNumber.value}:${smsMessage.value}`, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(`smsto:${smsNumber.value}:${smsMessage.value}`, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'wifi':
      QRCode.toCanvas(canvas, `WIFI:S:${wifiSsid.value};T:WPA;P:${wifiPassword.value};;`, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(`WIFI:S:${wifiSsid.value};T:WPA;P:${wifiPassword.value};;`, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
    case 'contact':
      QRCode.toCanvas(canvas, `BEGIN:VCARD\nVERSION:3.0\nFN:${contact.value.name}\nEMAIL:${contact.value.email}\nTEL:${contact.value.phone}\nURL:${contact.value.website}\nEND:VCARD`, { width: 500 }, (error) => {
        if (error) console.error(error);
      });
      QRCode.toDataURL(`BEGIN:VCARD\nVERSION:3.0\nFN:${contact.value.name}\nEMAIL:${contact.value.email}\nTEL:${contact.value.phone}\nURL:${contact.value.website}\nEND:VCARD`, { width: 500 }, (error, uri) => {
        if (error) console.error(error);
        dataUri.value = uri;
      });
      break;
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

input,
textarea,
select {
  padding: 10px;
  font-size: 16px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 20px;
}

button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

canvas,
img{
  margin-top: 20px;
  max-width: 500px;
  width: 100%;
}

.fields {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
</style>